/**
 * LocationPicker - Typeahead City/State Selector
 * Uses Material-UI Autocomplete for fast city searching across USA
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  Autocomplete,
  TextField,
  CircularProgress,
  Alert
} from '@mui/material';
import { searchCities } from '../../services/apiService';
import type { Location } from '../../services/apiService';

interface CityOption {
  city: string;
  state: string;
  stateAbbr: string;
  lat: number;
  lng: number;
}

interface LocationPickerProps {
  open: boolean;
  onLocationSelect: (location: Location) => void;
  onClose: () => void;
  currentLocation?: Location | null;
}

const LocationPicker: React.FC<LocationPickerProps> = ({
  open,
  onLocationSelect,
  onClose,
  currentLocation
}) => {
  const [searchInput, setSearchInput] = useState('');
  const [options, setOptions] = useState<CityOption[]>([]);
  const [selectedCity, setSelectedCity] = useState<CityOption | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestQueryRef = useRef('');

  const handleSearch = useCallback(async (query: string) => {
    latestQueryRef.current = query;

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }

    if (!query || query.trim().length < 2) {
      setLoading(false);
      setError(null);
      setOptions([]);
      return;
    }

    setLoading(true);
    setError(null);

    searchTimeoutRef.current = setTimeout(async () => {
      const scheduledQuery = query;
      try {
        console.log('🔍 Searching for:', scheduledQuery);
        const results = await searchCities(scheduledQuery);
        console.log(`✅ Got ${results.length} results`);

        if (latestQueryRef.current === scheduledQuery) {
          setOptions(results);
        }
      } catch (err: any) {
        console.error('❌ Search error:', err);
        if (latestQueryRef.current === scheduledQuery) {
          setError('Failed to search cities');
          setOptions([]);
        }
      } finally {
        if (latestQueryRef.current === scheduledQuery) {
          setLoading(false);
        }
      }
    }, 300);
  }, []);

  const handleClose = () => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
    latestQueryRef.current = '';
    setLoading(false);
    setSearchInput('');
    setOptions([]);
    setSelectedCity(null);
    setError(null);
    onClose();
  };

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleConfirm = () => {
    if (selectedCity) {
      const location: Location = {
        city: selectedCity.city,
        state: selectedCity.state,
        lat: selectedCity.lat,
        lng: selectedCity.lng
      };
      console.log('📍 Location selected:', location);
      onLocationSelect(location);
      handleClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)'
        }
      }}
    >
      <DialogTitle sx={{ 
        textAlign: 'center',
        background: 'linear-gradient(45deg, #667eea, #764ba2)',
        color: 'white',
        fontWeight: '700',
        fontSize: '1.3rem'
      }}>
        📍 Select Your Location
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
          Start typing a city name or state to find your location
        </Typography>

        {/* City Search Autocomplete */}
        <Autocomplete
          freeSolo
          options={options}
          getOptionLabel={(option) =>
            typeof option === 'string'
              ? option
              : `${option.city}, ${option.stateAbbr}`
          }
          value={selectedCity}
          onChange={(event, newValue) => {
            console.log('Selected:', newValue);
            if (newValue && typeof newValue !== 'string') {
              setSelectedCity(newValue);
            }
          }}
          inputValue={searchInput}
          onInputChange={(event, newInputValue) => {
            setSearchInput(newInputValue);
            setSelectedCity(null);
            handleSearch(newInputValue);
          }}
          loading={loading}
          renderInput={(params) => (
            <TextField
              {...params}
              label="City, State or ZIP"
              placeholder="e.g., 'New York' or 'Appleton, WI'"
              fullWidth
              autoFocus
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => {
            const { key, ...otherProps } = props;
            return (
              <Box component="li" key={key} {...otherProps}>
                <Box>
                  <Typography variant="body2">
                    {option.city}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#999' }}>
                    {option.state} ({option.stateAbbr})
                  </Typography>
                </Box>
              </Box>
            );
          }}
          noOptionsText={searchInput.length < 2 ? 'Type at least 2 characters' : 'No cities found'}
          ListboxProps={{
            sx: { maxHeight: 300 }
          }}
        />

        {/* Selected Location Display */}
        {selectedCity && (
          <Box sx={{ mt: 3, p: 2, backgroundColor: '#f0f4ff', borderRadius: '8px' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              ✅ Selected Location:
            </Typography>
            <Typography variant="body1" sx={{ mt: 1 }}>
              {selectedCity.city}, {selectedCity.state}
            </Typography>
            <Typography variant="caption" sx={{ color: '#666', display: 'block', mt: 1 }}>
              📍 {selectedCity.lat.toFixed(4)}, {selectedCity.lng.toFixed(4)}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button 
          onClick={handleClose}
          sx={{ 
            borderRadius: '20px',
            textTransform: 'none',
            fontSize: '1rem'
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={!selectedCity}
          variant="contained"
          sx={{
            borderRadius: '20px',
            background: 'linear-gradient(45deg, #667eea, #764ba2)',
            textTransform: 'none',
            fontSize: '1rem',
            fontWeight: 600,
            px: 3
          }}
        >
          Confirm Location
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LocationPicker;
